import ArchitectureChip from '@components/common/ArchitectureChip';
import DataGrid from '@components/common/DataGrid';
import EntitlementActionsMenu from './EntitlementActionsMenu';
import EntitlementQuotaBar from '@components/common/quota/EntitlementQuotaBar';
import Loading from '@components/common/Loading';
import PropTypes from 'prop-types';
import QuotaAdjustModal from './QuotaAdjustModal';
import QuotaHealthCards from './QuotaHealthCards';
import React, {useCallback, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArchitectureType,
  EntitlementStatus,
  getEntitlementPrimaryQuota,
  statusColorMap,
} from '@account/utils/entitlements';
import {DateTime} from 'luxon';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useOrgEntitlementsSummary} from '@hooks/useEntitlements';

const formatDate = (iso, fallback = '') => {
  if (!iso) return fallback;
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED);
};

const buildColumns = onAdjustQuota => [
  {
    field: 'architecture',
    headerName: 'Arch',
    width: 90,
    renderCell: ({value}) => <ArchitectureChip architecture={value} />,
  },
  {
    field: 'name',
    headerName: 'Product',
    minWidth: 200,
    flex: 0.4,
    renderCell: ({row}) => (
      <Typography variant="body2" fontWeight={500}>
        {row.name}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: '',
    width: 50,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({row}) => {
      const color =
        statusColorMap[row.status] === 'success'
          ? 'success.main'
          : statusColorMap[row.status] === 'warning'
            ? 'warning.main'
            : statusColorMap[row.status] === 'error'
              ? 'error.main'
              : 'grey.400';
      return (
        <Tooltip title={row.status}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: color,
            }}
          />
        </Tooltip>
      );
    },
  },
  {
    field: 'customerAccountName',
    headerName: 'Customer Account',
    width: 160,
    renderCell: ({value}) => (
      <Typography variant="body2" noWrap>
        {value || ''}
      </Typography>
    ),
  },
  {
    field: 'dates',
    headerName: 'Term',
    width: 150,
    valueGetter: (value, row) => row?.startDate ?? '',
    renderCell: ({row}) => (
      <Stack spacing={0}>
        <Typography variant="caption" color="textSecondary">
          {formatDate(row.startDate)}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {formatDate(row.endDate, 'Ongoing')}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'quota',
    headerName: 'Quota',
    flex: 0.6,
    minWidth: 200,
    sortable: false,
    renderCell: ({row}) => {
      const primary = getEntitlementPrimaryQuota(row);
      return <EntitlementQuotaBar quota={primary} />;
    },
  },
  {
    field: 'actions',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: ({row}) => (
      <EntitlementActionsMenu entitlement={row} onAdjustQuota={onAdjustQuota} />
    ),
  },
];

const OrgEntitlementsPage = ({organization}) => {
  const auth = useAuth();
  const orgId = organization?.id;
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  const [showExpired, setShowExpired] = useState(false);
  const [viewMode, setViewMode] = useState('all');
  const [adjustTarget, setAdjustTarget] = useState(null);

  const handleAdjustQuota = useCallback(
    entitlement => setAdjustTarget(entitlement),
    []
  );

  const columns = useMemo(
    () => buildColumns(isPlanetAdmin ? handleAdjustQuota : undefined),
    [isPlanetAdmin, handleAdjustQuota]
  );

  // When "Show expired" is on, pass undefined so the hook returns all statuses.
  // Otherwise restrict to active + pending.
  const activeStatusFilter = useMemo(
    () => [EntitlementStatus.ACTIVE, EntitlementStatus.PENDING],
    []
  );

  const {data, isLoading, error, summary} = useOrgEntitlementsSummary(orgId, {
    statusFilter: showExpired ? undefined : activeStatusFilter,
  });

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (viewMode === 'all') return data;
    if (viewMode === 'workspace') {
      return data.filter(e => e.architecture === ArchitectureType.WORKSPACE);
    }
    if (viewMode === 'classic') {
      return data.filter(e => e.architecture === ArchitectureType.CLASSIC);
    }
    return data;
  }, [data, viewMode]);

  if (!orgId || isLoading) return <Loading />;

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {organization?.name}
      </Typography>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          Failed to load some entitlement data. Partial results may be shown.
        </Alert>
      )}

      {summary && <QuotaHealthCards summary={summary} />}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={3}
        mb={2}
      >
        <Typography variant="h6">Entitlements</Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="workspace">Workspace</ToggleButton>
            <ToggleButton value="classic">Classic</ToggleButton>
          </ToggleButtonGroup>

          <FormControlLabel
            control={
              <Switch
                checked={showExpired}
                onChange={e => setShowExpired(e.target.checked)}
                size="small"
              />
            }
            label="Show expired"
          />
        </Box>
      </Box>

      <DataGrid
        rows={filteredData}
        columns={columns}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {paginationModel: {pageSize: 25}},
          sorting: {sortModel: [{field: 'name', sort: 'asc'}]},
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
        getRowHeight={() => 52}
      />

      <QuotaAdjustModal
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        entitlement={adjustTarget}
      />
    </Box>
  );
};

OrgEntitlementsPage.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
};

export default OrgEntitlementsPage;
